import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import NavBar from "../common/NavBar";
import Footer from "../common/Footer";
import OwnerMenu from "./OwnerMenu";
import { useTranslation } from "react-i18next";

const OwnerBlog = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState(
    ((i18n.language || "en").slice(0, 2).toLowerCase())
  );

  const getApiBaseUrl = () =>
    process.env.REACT_APP_API_URL || "http://localhost:8000";

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return {
      "Content-Type": "application/json",
      Authorization: `Token ${token}`,
    };
  };

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let url = `${getApiBaseUrl()}/api/blog/articles/?`;
      if (selectedLanguage && selectedLanguage !== "all") {
        url += `language=${encodeURIComponent(selectedLanguage)}&`;
      } else {
        url += `language=all&`;
      }
      if (searchQuery) {
        url += `search=${encodeURIComponent(searchQuery)}`;
      }
      const response = await fetch(url, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error(t("error_fetching_posts"));
      const data = await response.json();
      setArticles(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, selectedLanguage, t]);

  useEffect(() => {
    // Redirect if not owner/staff
    const rawGroups = localStorage.getItem("groups");
    let groups = [];
    if (rawGroups) {
      try {
        const parsed = JSON.parse(rawGroups);
        groups = Array.isArray(parsed) ? parsed : [parsed];
      } catch {
        groups = [rawGroups];
      }
    }
    if (!groups.includes("Owners") && !groups.includes("Staff")) {
      navigate("/login");
      return;
    }
    fetchArticles();
  }, [fetchArticles, navigate]);

  const handleDelete = async (slug) => {
    if (!window.confirm(t("confirm_delete_post"))) return;
    try {
      const response = await fetch(
        `${getApiBaseUrl()}/api/blog/articles/${slug}/`,
        { method: "DELETE", headers: getAuthHeaders() }
      );
      if (response.ok || response.status === 204) {
        setArticles((prev) => prev.filter((a) => a.slug !== slug));
        alert(t("post_deleted_successfully"));
      } else {
        alert(t("error_deleting_post"));
      }
    } catch {
      alert(t("error_deleting_post"));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <NavBar />
      <div className="flex flex-1 pt-16">
        <OwnerMenu />

        {/* Main content — offset by sidebar width (w-48 = 12rem) */}
        <main className="ml-48 flex-1 p-8">
          {/* Page header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                {t("blog_management")}
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                {t("blog_subtitle")}
              </p>
            </div>
            <Link
              to="/owner/blog/new"
              className="bg-blue-500 hover:bg-blue-600 text-white font-medium px-5 py-2 rounded-lg text-sm transition"
            >
              + {t("new_post")}
            </Link>
          </div>

          {/* Filters */}
          <div className="mb-6 flex flex-wrap gap-3 items-center">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("search_posts")}
              className="border border-gray-300 rounded-lg px-4 py-2 text-sm w-72 focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
            />
            <select
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              className="border border-gray-300 rounded-lg px-4 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <option value="all">{t("all_languages")}</option>
              <option value="de">DE</option>
              <option value="en">EN</option>
              <option value="es">ES</option>
            </select>
          </div>

          {/* Table card */}
          <div className="bg-white rounded-xl shadow overflow-hidden">
            {loading ? (
              <p className="text-center text-gray-500 py-16">{t("loading")}</p>
            ) : error ? (
              <p className="text-center text-red-500 py-16">{error}</p>
            ) : articles.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-gray-500 mb-4">{t("no_posts_found")}</p>
                <Link
                  to="/owner/blog/new"
                  className="bg-blue-500 hover:bg-blue-600 text-white px-5 py-2 rounded-lg text-sm transition"
                >
                  {t("new_post")}
                </Link>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left font-semibold text-gray-600 w-16">
                      {t("cover_image")}
                    </th>
                    <th className="px-6 py-3 text-left font-semibold text-gray-600">
                      {t("post_title")}
                    </th>
                    <th className="px-6 py-3 text-left font-semibold text-gray-600">
                      {t("status")}
                    </th>
                    <th className="px-6 py-3 text-left font-semibold text-gray-600 hidden md:table-cell">
                      {t("language")}
                    </th>
                    <th className="px-6 py-3 text-left font-semibold text-gray-600 hidden md:table-cell">
                      {t("tags")}
                    </th>
                    <th className="px-6 py-3 text-left font-semibold text-gray-600 hidden lg:table-cell">
                      {t("published_on")}
                    </th>
                    <th className="px-6 py-3 text-right font-semibold text-gray-600">
                      {t("actions")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {articles.map((article) => (
                    <tr key={article.slug} className="hover:bg-gray-50 transition">
                      {/* Cover thumbnail */}
                      <td className="px-6 py-4">
                        {article.cover_image_url ? (
                          <img
                            src={article.cover_image_url}
                            alt={article.title}
                            className="w-12 h-12 object-cover rounded-lg"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-400 font-bold text-lg">
                            {article.title.charAt(0).toUpperCase()}
                          </div>
                        )}
                      </td>

                      {/* Title */}
                      <td className="px-6 py-4">
                        <p className="font-medium text-gray-800 line-clamp-2">
                          {article.title}
                        </p>
                        {article.meta_description && (
                          <p className="text-gray-400 text-xs mt-0.5 line-clamp-1">
                            {article.meta_description}
                          </p>
                        )}
                      </td>

                      {/* Status badge */}
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            article.status === "published"
                              ? "bg-green-100 text-green-700"
                              : "bg-yellow-100 text-yellow-700"
                          }`}
                        >
                          {article.status === "published"
                            ? t("published")
                            : t("draft")}
                        </span>
                      </td>

                      <td className="px-6 py-4 hidden md:table-cell">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 uppercase">
                          {article.language}
                        </span>
                      </td>

                      {/* Tags */}
                      <td className="px-6 py-4 hidden md:table-cell">
                        <div className="flex flex-wrap gap-1">
                          {article.tags && article.tags.length > 0 ? (
                            article.tags.slice(0, 3).map((tag) => (
                              <span
                                key={tag}
                                className="bg-blue-100 text-blue-600 text-xs px-2 py-0.5 rounded-full"
                              >
                                {tag}
                              </span>
                            ))
                          ) : (
                            <span className="text-gray-300 text-xs">—</span>
                          )}
                        </div>
                      </td>

                      {/* Date */}
                      <td className="px-6 py-4 text-gray-400 hidden lg:table-cell whitespace-nowrap">
                        {formatDate(article.published_date)}
                      </td>

                      {/* Actions */}
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Link
                            to={`/blog/${article.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-400 hover:text-blue-500 text-xs transition px-2 py-1 rounded"
                            title="View"
                          >
                            &#8599;
                          </Link>
                          <Link
                            to={`/owner/blog/${article.slug}/edit`}
                            className="bg-blue-500 hover:bg-blue-600 text-white text-xs px-3 py-1.5 rounded-lg transition font-medium"
                          >
                            {t("update")}
                          </Link>
                          <button
                            onClick={() => handleDelete(article.slug)}
                            className="bg-red-500 hover:bg-red-600 text-white text-xs px-3 py-1.5 rounded-lg transition font-medium"
                          >
                            {t("delete")}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Article count */}
          {!loading && !error && articles.length > 0 && (
            <p className="text-xs text-gray-400 mt-3">
              {articles.length} {articles.length === 1 ? "post" : "posts"}
            </p>
          )}
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default OwnerBlog;
